import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useMe } from '../../api/hooks';
import { useClientsSummary } from './hooks';
import { ListScreen, ListRow, type ListTab } from '../../components/ListScreen';

const TABS: ListTab[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' }
];

function money(n: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return n.toFixed(2);
  }
}

export function CustomerListPage() {
  usePageTitle('Clients');
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const debounced = useDebouncedValue(search, 300);
  const { data: me } = useMe(true);
  const currency = me?.settings.defaultCurrency ?? 'USD';

  // Existing hook only exposes includeInactive; the Inactive tab filters client-side
  // (no API change per B2 constraints). Inactive clients are typically few.
  const { data, isLoading, isFetching } = useClientsSummary({
    search: debounced,
    includeInactive: tab !== 'active',
    page,
    pageSize: 25
  });

  const clients = (data?.clients ?? []).filter((c) => (tab === 'inactive' ? !c.isActive : true));
  const totalPages = data && tab !== 'inactive' ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const changeTab = (k: string) => {
    setTab(k);
    setPage(1);
  };

  return (
    <ListScreen
      title="Clients"
      tabs={TABS}
      activeTab={tab}
      onTabChange={changeTab}
      search={{
        value: search,
        onChange: (v) => {
          setSearch(v);
          setPage(1);
        },
        placeholder: 'Search name, email, phone…'
      }}
      primaryAction={{ label: 'New Client', to: '/customers/new' }}
      secondaryAction={{ label: 'Import vCard', to: '/customers/import' }}
      isEmpty={!isLoading && clients.length === 0}
      empty={{
        icon: <Users size={40} />,
        message:
          debounced.length >= 2
            ? `No clients match “${debounced}”.`
            : tab === 'inactive'
              ? 'No inactive clients.'
              : 'No clients yet.',
        action:
          debounced.length < 2 && tab !== 'inactive' ? (
            <Link to="/customers/new" className="text-sm font-medium text-pine hover:text-pine-600">
              Add your first client
            </Link>
          ) : undefined
      }}
    >
      {isLoading ? (
        <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          {clients.map((c) => (
            <ListRow
              key={c.id}
              to={`/customers/${c.id}`}
              primary={c.displayName}
              badge={!c.isActive ? <Inactive /> : undefined}
              secondary={`${c.email ?? c.phone ?? 'No contact'} · ${c.visitCount} visit${c.visitCount === 1 ? '' : 's'}`}
              right={money(c.totalOutstanding, currency)}
              rightSub={c.totalOutstanding > 0 ? 'outstanding' : 'paid up'}
            />
          ))}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 px-4 py-3 text-sm text-slate-600">
              <button
                type="button"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage(page - 1)}
                className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
              >
                Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage(page + 1)}
                className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </ListScreen>
  );
}

function Inactive() {
  return (
    <span className="inline-flex items-center rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
      Inactive
    </span>
  );
}
